/**
 * Forms Fields management and manipulation
 * @Author: Junaid Atari junaid.attari@invozone.dev
 * @Date: 2025-02-18 13:54:19
 */

import {snake, capital} from 'case';

// client
import ApiClient from '~/client/ApiClient';

// helpers
import {
  fetchCustomFields,
  fetchGeneralSettings,
  fetchProductDetails,
  fetchSelectDates,
  fetchTemplatesApi,
} from '~/helpers/api';

// types
import type {FieldInfo, HandleFields, ListItem} from '~/types/field.types';
import type {HandleType} from '~/helpers/shopify';
import type {
  DateOption, ICustomField, IGeneralSettings, IProductDetail, ITemplate,
} from '~/types/api.types';

/**
 * A callback function to be called when the form is submitted
 * @param fields The form fields
 */
export type DeleteOnComplete = (responses: RequestResponses) => void;

export interface RequestResponses {
  0: ITemplate[];
  1: DateOption[];
  2: IProductDetail | IProductDetail[];
  3: ICustomField[];
  4: IGeneralSettings;
}

export type FormFieldsManagerInstance = InstanceType<typeof FormFieldsManager>;

export default class FormFieldsManager<TFields extends HandleFields = HandleFields> {
  /**
   * Fetch responses from the APIs
   * @private
   */
  private responses: RequestResponses | undefined;

  /**
   * Constructor
   * @param apiClient - The API client instance
   * @param handle - The handle name
   * @param formFields - The form fields schema
   */
  constructor(
    private apiClient: InstanceType<typeof ApiClient>,
    private handle: HandleType, private formFields: FieldInfo[]) {
  }

  /**
   * Fetches the account specific data
   */
  async fetchData(): Promise<void> {
    this.responses = await Promise.all([
      fetchTemplatesApi(this.apiClient, this.handle),
      fetchSelectDates(this.apiClient),
      fetchProductDetails(this.apiClient, this.handle),
      fetchCustomFields(this.apiClient),
      fetchGeneralSettings(this.apiClient),
    ]);
  }

  /**
   * Returns the account general settings
   */
  get generalSettings(): IGeneralSettings {
    if (this.responses?.[4] === undefined)
      throw new Error('General settings not fetched yet');

    return this.responses[4] as IGeneralSettings;
  }

  /**
   * Returns the account specific custom fields
   */
  private get customFields(): FieldInfo[] {
    const customFields = this.responses?.[3] ?? [];

    if (!customFields.length)
      return [] as FieldInfo[];

    // snake
    const fields: FieldInfo[] = [];

    for (const customField of customFields) {
      fields.push({
        key: snake(customField.key),
        label: capital(customField.key.replace(/(CF\.)|\{+|}+/g, '')),
        category: 'customField',
        placeholder: 'e.g., My value',
        type: 'text',
      });
    }

    return fields;
  }

  /**
   * Initializes the form fields schema and populate values
   */
  initializeFields(): void {
    const responses = this.getResponses();

    //<editor-fold desc="Templates">
    const templates: ListItem[] = (responses[0] ?? [])?.map?.(t => ({
      value: t.id,
      label: `${t.title} - (${t.product.name})`,
    })) ?? [];

    this.updateByKey('select_template', 'items', templates);
    //</editor-fold>

    //<editor-fold desc="Select dates">
    this.updateByKey('select_date', 'items', responses[1]);
    //</editor-fold>

    //<editor-fold desc="Property address fields">
    if (!responses[4].propertyAddressStrictness) {
      this.removeByCategory('propertyAddress');
    }
    //</editor-fold>

    if (this.customFields.length && !this.hasField(this.customFields?.at?.(0)?.key)) {
      this.formFields.push(...this.customFields);
    }
  }

  /**
   * Returns the fetched responses list
   */
  getResponses(): RequestResponses {
    if (!this.responses)
      throw new Error('Responses not fetched yet');

    return this.responses;
  }

  /**
   * Find the form field by the given key
   * @param key - The key to search for
   * @returns The found form field
   * @throws Error if the field is not found
   */
  findByKey(key: keyof TFields): FieldInfo | null {
    const field = this.formFields.find(f => f.key === key);

    if (field) return field;

    return null;
  }

  /**
   * Find the form field by the given key
   * @param key - The key to search for
   * @returns The found form field
   * @throws Error if the field is not found
   */
  findByKeyRequired(key: keyof TFields): FieldInfo {
    const field = this.formFields.find(f => f.key === key);

    if (field) return field;

    throw new Error(`Field ${key as string} not found`);
  }

  /**
   * Find the list of form fields by the given category
   * @param name - The category to search for
   * @returns The form field list
   */
  findByCategory(name: FieldInfo['category']): FieldInfo[] {
    return this.formFields.filter(f => f.category === name);
  }

  /**
   * Checks if the given category exists
   * @param name - The category to search for
   * @return True if the category exists or false otherwise
   */
  hasCategory(name: FieldInfo['category']): boolean {
    return this.formFields.findIndex(f => f.category === name) !== -1;
  }

  /**
   * Remove the form fields of the given category
   * @param name - The name of the category
   */
  removeByCategory(name: FieldInfo['category']) {
    this.formFields = this.formFields.filter(f => f.category !== name);
  }

  /**
   * Remove the form field by the given key
   * @param key - The key to remove
   * @return True if the field was removed, false otherwise
   */
  removeField(key: keyof TFields): boolean {
    const fieldIndex = this.formFields.findIndex(x => x.key === key);

    if (fieldIndex === -1) {
      return false;
    }

    this.formFields.splice(fieldIndex, 1);
    return true;
  }

  /**
   * Checks if the given form field key exists
   * @param key - The key to search for
   * @return True if the key exists or false otherwise
   */
  hasField(key: keyof TFields): boolean {
    return this.formFields.some(x => x.key === key);
  }

  /**
   * Find the form field by key and update the form field's value by property name
   * @param key - The key to search for
   * @param name - Target property name
   * @param value - The value to update
   */
  updateByKey(key: keyof TFields, name: keyof Omit<FieldInfo, 'key' | 'type'>, value: any) {
    const fieldIndex = this.formFields.findIndex(x => x.key === key);

    if (fieldIndex === -1) {
      throw new Error(`Field ${key as string} not found`);
    }

    const formField: FieldInfo = this.formFields[fieldIndex];

    if (['key', 'type'].includes(key as string)) {
      throw new Error(`Cannot change readonly property '${key as string}'.`);
    }

    if (!Object.hasOwn(formField, name)) {
      throw new Error(`Unknown property name '${name as string}'.`);
    }

    this.formFields[fieldIndex][name as keyof FieldInfo] = value;
  }

  /**
   * Returns all the form fields
   */
  getFields(): FieldInfo[] {
    return Array.from(this.formFields);
  }

  /**
   * Apply the specified filters and return the corresponding form fields
   * @param [keys] - The list of keys to be ignored
   * @param [categories] - The list of categories to be ignored
   * @returns The filtered form fields
   */
  getFilteredFields(keys: (keyof TFields) [] = [], categories: (FieldInfo['category'])[] = []): FieldInfo[] {
    const formFields = Array.from(this.formFields);
    return formFields.filter(x => {
      if (keys.length && keys.includes(x.key))
        return false;

      return !(categories.length && (x.category && categories.includes(x.category)));
    });
  }

  /**
   * Get initial form-fields values
   */
  getInitialValues(): Record<keyof TFields, any> {
    const values = {
      select_template: undefined,
      select_date: undefined,
      first_name: '{customer.firstName}',
      last_name: '{customer.lastName}',
      company_name: '{customer.company}',
      address_1: '{customer.address1}',
      address_2: '{customer.address2}',
      city: '{customer.city}',
      state: '{customer.state}',
      zip: '{customer.zip}',
      phone_no: '{customer.phone}',
      email: '{customer.email}',
    } as Record<keyof TFields, any>;

    const keysToIgnore = Object.keys(values) as (keyof TFields)[];

    this.getFields().filter((x) => {
      return !keysToIgnore.includes(x.key as keyof TFields);
    }).forEach(x => {
      values[x.key as keyof TFields] = undefined;
    });

    return values;
  }

  /**
   * Normalizes the form values
   * @param values the values to normalize
   * @param [onCastValue] A callback function fires after the value is casted, you can process further process the value.
   */
  normalizeValues (values: Record<keyof TFields, any>, onCastValue: ((value: any, key: string, field: FieldInfo) => any) | undefined = undefined): Record<keyof TFields, any> {
    const newValues = {} as Record<keyof TFields, any>;

    for (const [key, value] of Object.entries(values)) {
      const fieldInfo = this.findByKey(key as keyof TFields);

      if ( fieldInfo === null) {
        continue;
      }

      const newValue = !String(value || '').trim().length ? (fieldInfo?.defaultValue ?? '') : value;
      let castedValue = fieldInfo?.cast?.(newValue) ?? newValue;

      if ( typeof onCastValue === 'function')
        castedValue = onCastValue(castedValue, key, fieldInfo);

      newValues[key as keyof TFields] = castedValue;
    }

    return newValues;
  }
}
